import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PowerIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/solid";
import { useAppContext } from "../utils/context/AppContext";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedDistrict,
  setSelectedDistrictCode,
  setSelectedDivision,
  setSelectedDivisionCode,
  setSelectedRegion,
  setSelectedRegionCode,
  setSelectedTaluk,
  setSelectedTalukCode,
} from "../components/filter/districtDetailsFilter/store";
import MenuCustomHook from "./MenuCustomHook";
import { setMenuData } from "./store";
import tnlogo from "../assets/tnlogo.png";
import DynamicIcon from "../components/Form/FormFields/DynamicIcon";
import { getFetchData } from "../utils/api/FetchData";
import { APICONSTANT } from "../utils/api/Config";
import { Icon } from "@iconify/react";
import {
  setSelectedFline,
  setSelectedIsd,
  setSelectedNisd,
} from "../components/chart/store";
import {
  setSelectedFlineCode,
  setSelectedIsdCode,
  setSelectedMediumCode,
  setSelectedMediumData,
  setSelectedNisdCode,
} from "../components/filter/stateDetailsFilter/store";
import { setChartListData } from "../pages/dashboard/store";
import { Landmark } from "lucide-react";
import { getSessionItem } from "../utils/cookie/Cookie";

export default function Sidebar() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile menu state
  const [username, setUsername] = useState("");
  const { signOut, setLoggingOut } = useAppContext();
  const { menuList } = MenuCustomHook();
  const dispatch = useDispatch();
  const location = useLocation(); // ✅ Needed for active menu

  const menuItems = useSelector((state: RootState) => state?.menu?.menu_data);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };
  const navigate = useNavigate();

  const signout = async () => {
    setLoggingOut(true);
    const response = await getFetchData(1, dispatch, APICONSTANT.loginOut);
    if (response?.status == 1) {
      signOut();
      dispatch(setSelectedRegionCode([]));
      dispatch(setSelectedDivisionCode([]));
      dispatch(setSelectedDistrictCode([]));
      dispatch(setSelectedTalukCode([]));
      setSidebarOpen(false);
    } else {
      console.error("Logout failed");
    }
    setLoggingOut(false);
  };

  const menuRes = async () => {
    const menuResponse = await menuList();
    dispatch(setMenuData(menuResponse));
  };

  useEffect(() => {
    menuRes();
    // Fetch username
    const fetchUser = async () => {
      const savedUser = await getSessionItem("username");
      if (savedUser) {
        setUsername(savedUser);
      }
    };
    fetchUser();
  }, []);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="sm:hidden fixed top-4 left-4 z-50 p-2 bg-red-900 text-white rounded-md"
        onClick={() => setSidebarOpen(true)}
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-full sm:w-full md:w-full lg:w-[7%] bg-[#1F1E2E] text-white font-light shadow-lg transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } sm:translate-x-0 sm:relative z-40`}
      >
        {/* Logo Header */}
        <div className="flex flex-col items-center justify-center mt-6 mb-4 px-2">
          {/* Blue circle user icon */}
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shadow-md">
            <Icon icon="mdi:account" className="text-[#1F1E2E] text-3xl" />
          </div>

          {/* Dynamic account/user label */}
          <span className="mt-3 text-xs font-semibold text-gray-200 tracking-wide break-words whitespace-normal text-center w-full">
            {username}
          </span>

          {/* Divider */}
          <div className="w-10/12 border-b border-gray-700 mt-3"></div>
        </div>

        {/* Navigation */}
        <nav className="mt-6 flex flex-col gap-2 items-center">
          {menuItems?.map((item, index) => {
            const isActive =
              item.path === location.pathname ||
              item?.subMenu?.some((sub) => sub.path === location.pathname);

            return (
              <div key={index} className="w-full text-center">
                {item?.subMenu?.length > 0 ? (
                  <>
                    <button
                      className={`flex flex-col items-center w-full py-3 transition-all ${
                        isActive ? "bg-blue-900" : "hover:bg-[#2a2b3b]"
                      }`}
                      onClick={() => {
                        toggleDropdown(index);
                      }}
                    >
                      <DynamicIcon
                        name={item.icon}
                        className={`h-6 w-6 mb-1 ${
                          isActive ? "text-white" : "text-blue-500"
                        }`}
                      />
                      <span className="text-xs">{item.name}</span>
                      <ChevronDownIcon
                        className={`h-4 w-4 mt-1 transition-transform ${
                          openDropdown === index ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openDropdown === index && item.subMenu && (
                      <div className="flex flex-col items-center gap-1 text-xs text-gray-300">
                        {item.subMenu.map((sub, i) => (
                          <a
                            key={i}
                            href={sub.path}
                            className={`hover:text-white px-2 py-1 transition-colors w-full text-center ${
                              sub.path === location.pathname
                                ? "bg-blue-800 text-white"
                                : ""
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            {sub.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.id == "11" ? "/aiassistant" : item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex flex-col items-center py-3 transition-all ${
                      isActive ? "bg-blue-900" : "hover:bg-[#2a2b3b]"
                    }`}
                  >
                    <DynamicIcon
                      name={item.icon}
                      className={`h-6 w-6 mb-1 ${
                        isActive ? "text-white" : "text-blue-500"
                      }`}
                    />
                    <span className="text-xs">{item.name}</span>
                  </Link>
                )}
              </div>
            );
          })}

          {/* Logout Button */}
          <Link
            to="/remarks"
            className="mt-2 text-gray-400 hover:text-blue-500 flex flex-col items-center py-3 transition-all cursor-pointe"
          >
            <Landmark className="h-6 w-6 mb-1" />{" "}
            <span className="text-xs">Remarks</span>
          </Link>
          {/* Logout Button */}
          <Link
            to="/"
            className="mt-6 text-gray-400 hover:text-red-500 flex flex-col items-center py-3 transition-all"
            onClick={signout}
          >
            <PowerIcon className="h-5 w-5 mb-1" />{" "}
            <span className="text-xs">Logout</span>
          </Link>
        </nav>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-transparent sm:hidden z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
}
